import { useEffect, useRef, type JSX } from 'react';

import { type AnyRouter } from '@tanstack/react-router';

import { useUser } from '@/store/useUser';

import { analyticsConfig, injectGTM, pushDataLayer, pushUserIdentity } from './index';

interface AnalyticsProviderProps {
  router: AnyRouter;
  children: React.ReactNode;
}

function firePageView(): void {
  // [plurallTracker] window.PLURALL_TRACKER?.pageView();
  if (analyticsConfig.isTrackedEnvironment) {
    pushDataLayer({ event: 'pageview', pagePath: window.location.pathname });
  }
}

export function AnalyticsProvider({ router, children }: AnalyticsProviderProps): JSX.Element {
  const { user } = useUser();
  const lastPushedUuidRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    injectGTM(analyticsConfig);
    // [plurallTracker] void initPlurallTracker(analyticsConfig).catch(console.error);

    // Initial page view
    firePageView();

    // Subscribe to route changes
    const unsubscribe = router.subscribe('onResolved', () => {
      firePageView();
    });

    return unsubscribe;
  }, [router]);

  useEffect(() => {
    if (user && analyticsConfig.environment !== 'development') {
      // Only push when the UUID changes to avoid duplicate events on re-renders
      if (user.uuid !== lastPushedUuidRef.current) {
        lastPushedUuidRef.current = user.uuid;
        pushUserIdentity(user, analyticsConfig);
      }
    }
  }, [user]);

  return <>{children}</>;
}
